import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const handleLogout = () => {
        localStorage.removeItem('adminVerified');
        window.location.href = '/';
    };

    // Close sidebar when route changes on mobile
    useEffect(() => {
        setIsSidebarOpen(false);
    }, [location.pathname]);

    // Close sidebar when clicking outside on mobile
    useEffect(() => {
        const handleClickOutside = (event) => {
            const sidebar = document.querySelector('.sidebar');
            const hamburger = document.querySelector('.hamburger-menu');
            
            if (sidebar && hamburger && 
                !sidebar.contains(event.target) && 
                !hamburger.contains(event.target) && 
                isSidebarOpen) {
                setIsSidebarOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isSidebarOpen]);

    return (
        <>
            <style>
                {`
                    .sidebar {
                        width: 280px;
                        height: 100vh;
                        background: white;
                        position: fixed;
                        left: 0;
                        top: 0;
                        box-shadow: 2px 0 4px rgba(0,0,0,0.1);
                        z-index: 1000;
                        transition: transform 0.3s ease;
                    }

                    .sidebar-content {
                        padding: 2rem;
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                    }

                    .sidebar-title {
                        color: #1a237e;
                        font-size: 1.5rem;
                        font-weight: 600;
                        margin-bottom: 2rem;
                    }

                    .nav-menu {
                        display: flex;
                        flex-direction: column;
                        gap: 0.5rem;
                    }

                    .menu-item {
                        padding: 1rem;
                        color: #333;
                        text-decoration: none;
                        border-radius: 8px;
                        transition: all 0.3s;
                        font-weight: 500;
                    }

                    .menu-item:hover {
                        background: rgba(48, 63, 159, 0.1);
                        color: #1a237e;
                    }

                    .menu-item.active {
                        background: #1a237e;
                        color: white;
                    }

                    .logout-button {
                        margin-top: auto;
                        padding: 1rem 1.5rem;
                        background: #f5f5f5;
                        color: #dc3545;
                        border: none;
                        border-radius: 8px;
                        cursor: pointer;
                        font-size: 1rem;
                        font-weight: 500;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 0.5rem;
                        transition: all 0.3s ease;
                        width: 100%;
                        margin-bottom: 1rem;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                    }

                    .logout-button:hover {
                        background: #dc3545;
                        color: white;
                        transform: translateY(-1px);
                        box-shadow: 0 4px 8px rgba(220, 53, 69, 0.2);
                    }

                    .logout-button:active {
                        transform: translateY(0);
                        box-shadow: 0 2px 4px rgba(220, 53, 69, 0.2);
                    }

                    .logout-icon {
                        font-size: 1.2rem;
                    }

                    /* Mobile hamburger menu */
                    .hamburger-menu {
                        display: none;
                        position: fixed;
                        top: 1rem;
                        left: 1rem;
                        z-index: 1001;
                        background: #1a237e;
                        color: white;
                        width: 50px;
                        height: 50px;
                        border-radius: 50%;
                        align-items: center;
                        justify-content: center;
                        cursor: pointer;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.2);
                        border: none;
                        outline: none;
                        transition: all 0.3s ease;
                    }

                    .hamburger-menu:hover {
                        background: #303f9f;
                    }

                    .hamburger-icon {
                        font-size: 1.5rem;
                    }

                    .content-wrapper {
                        transition: margin-left 0.3s ease;
                    }

                    /* Overlay for when sidebar is open on mobile */
                    .sidebar-overlay {
                        display: none;
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background: rgba(0,0,0,0.5);
                        z-index: 999;
                        opacity: 0;
                        transition: opacity 0.3s ease;
                        pointer-events: none;
                    }

                    @media (max-width: 768px) {
                        .sidebar {
                            transform: translateX(-100%);
                            width: 250px;
                        }

                        .sidebar.open {
                            transform: translateX(0);
                        }

                        .hamburger-menu {
                            display: flex;
                        }

                        .content-wrapper {
                            margin-left: 0 !important;
                        }

                        .sidebar-overlay {
                            display: block;
                        }

                        .sidebar-overlay.active {
                            opacity: 1;
                            pointer-events: all;
                        }
                    }

                    /* For desktop */
                    @media (min-width: 769px) {
                        .content-wrapper {
                            margin-left: 280px;
                        }
                    }
                `}
            </style>

            {/* Overlay for mobile */}
            <div 
                className={`sidebar-overlay ${isSidebarOpen ? 'active' : ''}`}
                onClick={() => setIsSidebarOpen(false)}
            ></div>

            {/* Hamburger menu for mobile */}
            <button 
                className="hamburger-menu" 
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                aria-label="Toggle menu"
            >
                <span className="hamburger-icon">{isSidebarOpen ? '✕' : '☰'}</span>
            </button>

            <div className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-content">
                    <h2 className="sidebar-title">SpinWin Admin</h2>
                    <nav className="nav-menu">
                        <a 
                            href="/" 
                            className={`menu-item ${location.pathname === '/' || location.pathname === '/wheel/details' || location.pathname === '/wheelEdit' || location.pathname === '/wheeledit' ? 'active' : ''}`}
                        >
                            Dashboard
                        </a>
                        <a 
                            href="/creatorList" 
                            className={`menu-item ${location.pathname === '/creatorList' || location.pathname === '/creator/list' ? 'active' : ''}`}
                        >
                            Creators
                        </a>
                        <a 
                            href="/subscribeList" 
                            className={`menu-item ${location.pathname === '/subscribeList' ||location.pathname === '/subscribeList/' ? 'active' : ''}`}
                        >
                            Subscribe List
                        </a>
                    </nav>
                    <button 
                        className="logout-button" 
                        onClick={handleLogout}
                    >
                        <span className="logout-icon">↪️</span>
                        Logout
                    </button>
                </div>
            </div>
        </>
    );
};

export default Sidebar;